import { ActionIcon, Group, Loader, Progress, Stack, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import type { ProductCatalogueEntry } from '../catalogue/products';
import type { ReadinessState } from '../lib/useGuardianReadiness';
import type { ProvisionStage } from '../lib/provisionStatus';

type ActiveState = Exclude<ReadinessState, 'idle'>;

const COPY: Record<ActiveState, { title: (name: string) => string; body: (name: string) => string; tone: string }> = {
  checking: {
    title: (name) => `Setting up your ${name}`,
    body: () => 'Usually a few minutes — the card below will light up when it’s ready.',
    tone: 'var(--text-subtle)',
  },
  ready: {
    title: (name) => `Your ${name} is ready`,
    body: () => "Everything's built and waiting for you.",
    tone: 'var(--status-success)',
  },
  'timed-out': {
    title: () => 'Taking longer than expected',
    body: (name) =>
      `We're still on it — your ${name} will light up here the moment it's ready. It's safe to leave this page.`,
    tone: 'var(--status-warning)',
  },
};

// The provisioner's stage names are contract (they arrive from the status endpoint);
// the customer-facing wording for each is owned here.
const STAGE_LABELS: Record<string, string> = {
  secrets: 'Credentials secured',
  identity: 'Space reserved',
  data: 'Creating your database',
  // gitops = deployment requested, not started — starting happens in the convergence wait below.
  gitops: 'Requesting deployment',
};

function StageRow({ stage }: Readonly<{ stage: ProvisionStage }>) {
  let icon;
  if (stage.state === 'succeeded') icon = <IconCheck size={13} color="var(--status-success)" aria-label="done" />;
  else if (stage.state === 'running') icon = <Loader size={12} color="var(--text-subtle)" />;
  else if (stage.state === 'failed') icon = <IconX size={13} color="var(--status-warning)" aria-label="failed" />;
  else icon = <Text component="span" size="xs" c="var(--text-subtle)" w={13} ta="center">·</Text>;

  return (
    <Group gap={8} wrap="nowrap">
      {icon}
      <Text size="xs" c={stage.state === 'pending' ? 'var(--text-subtle)' : 'var(--text-muted)'}>
        {STAGE_LABELS[stage.name] ?? stage.name}
      </Text>
    </Group>
  );
}

export function ProvisioningNotice({ product, state, stages, onClose }: Readonly<{
  product: ProductCatalogueEntry;
  state: ReadinessState;
  stages?: ProvisionStage[];
  onClose?: () => void;
}>) {
  if (state === 'idle') return null;
  const { title, body, tone } = COPY[state];
  const showSteps = state === 'checking' && !!stages && stages.length > 0;
  // Every step done but not ready = the convergence wait — real minutes on a fresh org.
  const converging = showSteps && stages.every((s) => s.state === 'succeeded');

  return (
    <Stack
      role={state === 'timed-out' ? 'alert' : 'status'}
      align="center"
      gap={6}
      mt="sm"
      style={{
        position: 'relative',
        textAlign: 'center',
        padding: '16px 18px',
        borderRadius: 8,
        border: `1px solid color-mix(in srgb, ${tone}, transparent 65%)`,
        background: `color-mix(in srgb, ${tone}, transparent 90%)`,
      }}
    >
      <img src={product.logo} alt="" style={{ width: 30, height: 'auto' }} />
      <Text size="sm" fw={700} c="var(--text-default)">{title(product.name)}</Text>
      {!showSteps && <Text size="xs" c="var(--text-muted)">{body(product.name)}</Text>}
      {showSteps && (
        <Stack gap={3} align="flex-start" mt={2}>
          {stages.map((stage) => <StageRow key={stage.name} stage={stage} />)}
          {converging && (
            <Group gap={8} wrap="nowrap">
              <Loader size={12} color="var(--text-subtle)" />
              <Text size="xs" c="var(--text-muted)">
                Bringing {product.name} online — first-time setup takes about 10 minutes
              </Text>
            </Group>
          )}
        </Stack>
      )}
      {state === 'checking' && !showSteps && (
        <Progress
          value={100}
          animated
          color={product.brandColour}
          size="xs"
          w={150}
          aria-label={`Setting up ${product.name}`}
        />
      )}
      {state === 'ready' && onClose && (
        <ActionIcon
          variant="subtle"
          size="sm"
          aria-label="Dismiss"
          onClick={onClose}
          style={{ position: 'absolute', top: 8, right: 8, '--ai-color': tone }}
        >
          <IconX size={16} />
        </ActionIcon>
      )}
    </Stack>
  );
}
